import { yupResolver } from "@hookform/resolvers/yup";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { i18_TRANSLATION } from "../../i18nReferences";
import { nextFieldFocusHandler } from "../utils/inputHandler";
import { renderLoginAlert, renderLoginResetAlert } from "./loginAlerts";
import { loginSchema } from "./loginSchema";

/**
 * Studio Login View Component
 * Pure presentation component for studio login with two-column layout
 * Left: Login form, Right: Signup information
 */
const LoginStudioView = ({
    t,
    onSubmit,
    isLoginFailed,
    isLoggedIn,
    successText,
    loginFailedText,
    nameText,
    onClickReset,
    loginResetError,
    loginResetResult,
}) => {
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
    } = useForm({
        resolver: yupResolver(loginSchema),
    });
    
    const name = useWatch({ control, name: "name" });


    return (
        (<Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <Box sx={{ maxWidth: "900px", width: "100%", px: { xs: 2, sm: 0 }, pb: 4 }}>
                <Grid container spacing={{ xs: 3, sm: 3 }} sx={{
                    alignItems: "flex-start"
                }}>
                    {/* Left Column - Login */}
                    <Grid size={{ xs: 12, sm: 5 }}>
                        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                            <Typography
                                variant="h5"
                                gutterBottom
                                sx={{
                                    color: "text.primary",
                                    fontWeight: 600,
                                    fontSize: { xs: "1.25rem", sm: "1.1rem", md: "1.5rem" }
                                }}>
                                {t("STUDIO_LOGIN.ALREADY_CUSTOMER_TITLE", { ns: i18_TRANSLATION })}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "text.secondary",
                                    mb: { xs: 3, sm: 2 }
                                }}>
                                {t("STUDIO_LOGIN.ALREADY_CUSTOMER_SUBTITLE", { ns: i18_TRANSLATION })}
                            </Typography>

                            <form onSubmit={handleSubmit(onSubmit)}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 2
                                    }}>
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        {...register("name")}
                                        onKeyUp={nextFieldFocusHandler}
                                        error={!!errors?.name}
                                        required
                                        variant="standard"
                                        label={nameText || t("STUDIO_LOGIN.USERNAME_LABEL", { ns: i18_TRANSLATION })}
                                    />
                                    <TextField
                                        fullWidth
                                        {...register("password")}
                                        error={!!errors?.password}
                                        required
                                        type="password"
                                        variant="standard"
                                        label={t("STUDIO_LOGIN.PASSWORD_LABEL", { ns: i18_TRANSLATION })}
                                    />
                                    <Box sx={{ mt: 2 }}>
                                        <Button
                                            type="submit"
                                            disabled={isLoggedIn}
                                            variant="contained"
                                            fullWidth
                                            data-testid="login-studio-submit"
                                        >
                                            {t("STUDIO_LOGIN.LOGIN_BUTTON", { ns: i18_TRANSLATION })}
                                        </Button>
                                    </Box>
                                    {renderLoginAlert(isLoginFailed, isLoggedIn, loginFailedText, successText)}
                                    {name?.length > 2 ? (
                                        <Box sx={{ mt: 1 }}>
                                            <Link onClick={() => onClickReset(name)} sx={{ cursor: "pointer" }}>
                                                {t("STUDIO_LOGIN.FORGOT_PASSWORD", { ns: i18_TRANSLATION })}
                                            </Link>
                                        </Box>
                                    ) : null}
                                    {renderLoginResetAlert(loginResetResult, loginResetError, t, "AUTH.RESET_STUDIO_LOGIN_SUCCESS_INFO")}
                                </Box>
                            </form>
                        </Box>
                    </Grid>

                    {/* Divider */}
                    <Grid size={{ xs: 12, sm: "auto" }} sx={{ display: "flex", justifyContent: "center", alignItems: "stretch", mx: { sm: 1 } }}>
                        <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", sm: "block" } }} />
                        <Divider sx={{ display: { xs: "block", sm: "none" }, width: "100%" }} />
                    </Grid>

                    {/* Right Column - Signup with Products Link */}
                    <Grid size={{ xs: 12, sm: 5 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", gap: 3 }}>
                            {/* Signup Card */}
                            <Box>
                                <Typography variant="h5" color="primary" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: "1.25rem", sm: "1.1rem", md: "1.5rem" } }}>
                                    {t("LOGIN_SIGNUP.NEW_AT_KISSCAL_TITLE", { ns: i18_TRANSLATION })}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "text.secondary",
                                        mb: { xs: 3, sm: 2 }
                                    }}>
                                    {t("LOGIN_SIGNUP.NEW_AT_KISSCAL_SUBTITLE", { ns: i18_TRANSLATION })}
                                </Typography>

                                <Card
                                    variant="outlined"
                                    sx={{
                                        bgcolor: "primary.50",
                                        borderColor: "primary.main",
                                        borderWidth: 2,
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                        p: { xs: 2, sm: 1.5 },
                                    }}
                                >
                                    <CardContent sx={{ textAlign: "center", p: { xs: 2, sm: 1 }, "&:last-child": { pb: { xs: 2, sm: 1 } } }}>
                                        <HowToRegIcon sx={{ fontSize: { xs: 60, sm: 48 }, color: "primary.main", mb: { xs: 2, sm: 1 } }} />
                                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: "1.25rem", sm: "1rem" } }}>
                                            {t("LOGIN_SIGNUP.REGISTER_NOW", { ns: i18_TRANSLATION })}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: "text.secondary",
                                                mb: { xs: 3, sm: 2 },
                                                fontSize: { xs: "0.875rem", sm: "0.75rem" }
                                            }}>
                                            • {t("LOGIN_SIGNUP.BENEFIT_30_DAYS", { ns: i18_TRANSLATION })}
                                            <br />
                                            • {t("LOGIN_SIGNUP.BENEFIT_50_SMS", { ns: i18_TRANSLATION })}
                                            <br />
                                            • {t("LOGIN_SIGNUP.BENEFIT_NO_AUTORENEW", { ns: i18_TRANSLATION })}
                                            <br />• {t("LOGIN_SIGNUP.BENEFIT_NO_CREDITCARD", { ns: i18_TRANSLATION })}
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            size="large"
                                            fullWidth
                                            onClick={() => navigate("/signup")}
                                            sx={{
                                                fontWeight: 600,
                                                py: { xs: 1.5, sm: 1 },
                                                fontSize: { xs: "1.1rem", sm: "0.9rem" },
                                            }}
                                        >
                                            {t("LOGIN_SIGNUP.START_TRIAL_BUTTON", { ns: i18_TRANSLATION })}
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Box>

                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Box>)
    );
};

export default LoginStudioView;

